import React, { useMemo, useRef } from 'react';
import { chartElementAtEvent } from '../chartInteraction';
import type { Chart as ChartJS } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import KpiChartPanel from './KpiChartPanel';
import { KPI_GREEN, KPI_LIGHT_BLUE, KPI_ORANGE, KPI_PRIMARY_BLUE } from './kpiChartPalette';

export interface CloseCodeSlice {
  key: string;
  label: string;
  count: number;
}

const SLICE_COLORS: Record<string, string> = {
  success: KPI_GREEN,
  cancelled: KPI_LIGHT_BLUE,
  failed: KPI_PRIMARY_BLUE,
  failed_impact: KPI_ORANGE,
};

export interface KpiChangeCloseCodeDonutProps {
  slices: CloseCodeSlice[];
  /** Drill-down: close-code bucket key (success | cancelled | failed | failed_impact). */
  onSliceClick?: (sliceKey: string) => void;
}

const KpiChangeCloseCodeDonut: React.FC<KpiChangeCloseCodeDonutProps> = ({ slices, onSliceClick }) => {
  const chartRef = useRef<ChartJS<'doughnut'>>(null);
  const total = useMemo(() => slices.reduce((s, x) => s + x.count, 0), [slices]);
  const labels = slices.map((s) => s.label);
  const data = slices.map((s) => s.count);
  const bg = slices.map((s) => SLICE_COLORS[s.key] ?? KPI_PRIMARY_BLUE);
  const sliceKeys = slices.map((s) => s.key);

  const handleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onSliceClick || !chartRef.current) return;
    const els = chartElementAtEvent(chartRef.current, event);
    const el = els[0];
    if (el == null) return;
    const idx = el.index;
    const key = sliceKeys[idx];
    const count = data[idx];
    if (!key || !count || count <= 0) return;
    onSliceClick(key);
  };

  if (!total) {
    return (
      <KpiChartPanel title="Changes by Close Code" className="h-full" flexFill titleClassName="text-center">
        <p className="px-4 py-10 text-center text-sm text-slate-500">No close-code data for the selected filters.</p>
      </KpiChartPanel>
    );
  }

  return (
    <KpiChartPanel title="Changes by Close Code" className="h-full" flexFill titleClassName="text-center">
      <div className="relative mx-auto flex min-h-[260px] max-w-md flex-1 flex-col items-center justify-center px-2 pb-2 pt-2">
        <Doughnut
          ref={chartRef}
          onClick={onSliceClick ? handleClick : undefined}
          data={{
            labels,
            datasets: [
              {
                data,
                backgroundColor: bg,
                borderColor: '#ffffff',
                borderWidth: 2,
                hoverOffset: 6,
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            cutout: '58%',
            onHover: (event, elements, chart) => {
              const canvas = chart.canvas;
              if (!onSliceClick) {
                canvas.style.cursor = 'default';
                return;
              }
              const el = elements[0];
              if (!el) {
                canvas.style.cursor = 'default';
                return;
              }
              const c = data[el.index];
              canvas.style.cursor = c > 0 ? 'pointer' : 'default';
            },
            plugins: {
              legend: {
                position: 'bottom',
                labels: {
                  boxWidth: 12,
                  padding: 12,
                  color: '#475569',
                  font: { size: 11 },
                },
              },
              tooltip: {
                callbacks: {
                  label: (ctx) => {
                    const v = Number(ctx.raw);
                    const pct = total > 0 ? ((v / total) * 100).toFixed(2) : '0';
                    return `${ctx.label}: ${v.toLocaleString()} (${pct}%)`;
                  },
                },
              },
              datalabels: {
                display: true,
                color: '#0f172a',
                font: { weight: 600, size: 10 },
                formatter: (value: number) => {
                  const v = Number(value);
                  if (v <= 0) return '';
                  const pct = ((v / total) * 100).toFixed(1);
                  return `${v.toLocaleString()}\n(${pct}%)`;
                },
              },
            },
          }}
        />
      </div>
    </KpiChartPanel>
  );
};

export default KpiChangeCloseCodeDonut;
